import { FC } from 'react';
import { ResponsiveLine } from '@nivo/line';

import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { ReportComponent } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import {
    averageNumericValues,
    filterZeroSeriesPoints,
    formatCompactChartValue,
    getAverageMarker,
    getBottomLegend,
    getCartesianChartMargin,
    getChartFormatting,
    humanizeChartLabel,
    normalizeChartPrimitive,
    truncateChartLabel,
} from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { useNivoReportData } from '../useNivoReportData';
import { useNivoReportFormatting } from '../useNivoReportFormatting';

interface LineChartProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

interface LineChartTooltipPoint {
    data: {
        x: unknown;
        y: unknown;
    };
    seriesColor: string;
    seriesId: number | string;
}

interface LineChartPointTooltipProps {
    point: LineChartTooltipPoint;
}

interface LineChartSliceTooltipProps {
    slice: {
        points: readonly LineChartTooltipPoint[];
    };
}

const LineChart: FC<LineChartProps> = ({ component, numerator, denominator }): JSX.Element => {
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();
    const { financialValueDisplay, formatMetricValue, formatDimensionValue, getMetricLabel, getDimensionLabel } =
        useNivoReportFormatting(component);
    const config = component.configuration as ReportComponentConfig;
    const formatting = getChartFormatting(config);

    const data = [
        {
            id: 'japan',
            color: 'hsl(101, 70%, 50%)',
            data: [
                {
                    x: 'plane',
                    y: 249,
                },
                {
                    x: 'helicopter',
                    y: 294,
                },
                {
                    x: 'boat',
                    y: 165,
                },
                {
                    x: 'train',
                    y: 283,
                },
                {
                    x: 'subway',
                    y: 264,
                },
                {
                    x: 'bus',
                    y: 29,
                },
                {
                    x: 'car',
                    y: 91,
                },
                {
                    x: 'moto',
                    y: 213,
                },
                {
                    x: 'bicycle',
                    y: 239,
                },
                {
                    x: 'horse',
                    y: 262,
                },
                {
                    x: 'skateboard',
                    y: 114,
                },
                {
                    x: 'others',
                    y: 113,
                },
            ],
        },
        {
            id: 'france',
            color: 'hsl(165, 70%, 50%)',
            data: [
                {
                    x: 'plane',
                    y: 52,
                },
                {
                    x: 'helicopter',
                    y: 9,
                },
                {
                    x: 'boat',
                    y: 182,
                },
                {
                    x: 'train',
                    y: 121,
                },
                {
                    x: 'subway',
                    y: 285,
                },
                {
                    x: 'bus',
                    y: 225,
                },
                {
                    x: 'car',
                    y: 68,
                },
                {
                    x: 'moto',
                    y: 30,
                },
                {
                    x: 'bicycle',
                    y: 64,
                },
                {
                    x: 'horse',
                    y: 145,
                },
                {
                    x: 'skateboard',
                    y: 256,
                },
                {
                    x: 'others',
                    y: 4,
                },
            ],
        },
        {
            id: 'us',
            color: 'hsl(236, 70%, 50%)',
            data: [
                {
                    x: 'plane',
                    y: 3,
                },
                {
                    x: 'helicopter',
                    y: 248,
                },
                {
                    x: 'boat',
                    y: 72,
                },
                {
                    x: 'train',
                    y: 37,
                },
                {
                    x: 'subway',
                    y: 258,
                },
                {
                    x: 'bus',
                    y: 201,
                },
                {
                    x: 'car',
                    y: 183,
                },
                {
                    x: 'moto',
                    y: 80,
                },
                {
                    x: 'bicycle',
                    y: 31,
                },
                {
                    x: 'horse',
                    y: 298,
                },
                {
                    x: 'skateboard',
                    y: 255,
                },
                {
                    x: 'others',
                    y: 250,
                },
            ],
        },
        {
            id: 'germany',
            color: 'hsl(354, 70%, 50%)',
            data: [
                {
                    x: 'plane',
                    y: 27,
                },
                {
                    x: 'helicopter',
                    y: 135,
                },
                {
                    x: 'boat',
                    y: 14,
                },
                {
                    x: 'train',
                    y: 91,
                },
                {
                    x: 'subway',
                    y: 4,
                },
                {
                    x: 'bus',
                    y: 71,
                },
                {
                    x: 'car',
                    y: 202,
                },
                {
                    x: 'moto',
                    y: 141,
                },
                {
                    x: 'bicycle',
                    y: 228,
                },
                {
                    x: 'horse',
                    y: 109,
                },
                {
                    x: 'skateboard',
                    y: 9,
                },
                {
                    x: 'others',
                    y: 157,
                },
            ],
        },
        {
            id: 'norway',
            color: 'hsl(141, 70%, 50%)',
            data: [
                {
                    x: 'plane',
                    y: 219,
                },
                {
                    x: 'helicopter',
                    y: 37,
                },
                {
                    x: 'boat',
                    y: 272,
                },
                {
                    x: 'train',
                    y: 259,
                },
                {
                    x: 'subway',
                    y: 286,
                },
                {
                    x: 'bus',
                    y: 10,
                },
                {
                    x: 'car',
                    y: 31,
                },
                {
                    x: 'moto',
                    y: 220,
                },
                {
                    x: 'bicycle',
                    y: 162,
                },
                {
                    x: 'horse',
                    y: 143,
                },
                {
                    x: 'skateboard',
                    y: 156,
                },
                {
                    x: 'others',
                    y: 16,
                },
            ],
        },
    ];
    const rawChartData = useNivoReportData(component, data);
    const chartData = filterZeroSeriesPoints(rawChartData, formatting.showZeroValues);
    const margin = getCartesianChartMargin({
        showAxisLabels: formatting.showAxisLabels,
        showLegend: formatting.showLegend,
    });
    const averageValue = averageNumericValues(chartData.flatMap((serie) => serie.data.map((point) => point.y)));
    const averageMarkers = formatting.showAverageLine
        ? getAverageMarker({
              axis: 'y',
              color: nivoChartTheme.textColor as string,
              financialValueDisplay,
              formatValue: formatMetricValue,
              value: averageValue,
          })
        : [];
    const lineTooltip = ({ point }: LineChartPointTooltipProps) => (
        <CustomNivoChartTooltip
            title={formatDimensionValue(normalizeChartPrimitive(point.data.x))}
            entries={[
                {
                    color: point.seriesColor,
                    label: humanizeChartLabel(point.seriesId),
                    value: normalizeChartPrimitive(point.data.y),
                },
            ]}
            formatValue={formatMetricValue}
        />
    );
    const sliceTooltip = ({ slice }: LineChartSliceTooltipProps) => (
        <CustomNivoChartTooltip
            title={formatDimensionValue(normalizeChartPrimitive(slice.points[0]?.data?.x))}
            entries={slice.points.map((point) => ({
                color: point.seriesColor,
                label: humanizeChartLabel(point.seriesId),
                value: normalizeChartPrimitive(point.data.y),
            }))}
            formatValue={formatMetricValue}
        />
    );

    return (
        <div
            style={{
                aspectRatio: `${numerator}/${denominator}`,
                height: '100%',
                minHeight: 0,
                width: '100%',
            }}
        >
            <ResponsiveLine
                data={chartData}
                margin={margin}
                xScale={{ type: 'point' }}
                yScale={{
                    type: 'linear',
                    min: 'auto',
                    max: 'auto',
                    stacked: true,
                    reverse: false,
                }}
                yFormat={(value) => formatMetricValue(value)}
                curve='natural'
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    tickSize: 4,
                    tickPadding: 8,
                    tickRotation: 0,
                    format: (value) => truncateChartLabel(formatDimensionValue(value), 18),
                    legend: formatting.showAxisLabels ? getDimensionLabel() : undefined,
                    legendOffset: 40,
                    legendPosition: 'middle',
                    truncateTickAt: 18,
                }}
                axisLeft={{
                    tickSize: 4,
                    tickPadding: 8,
                    tickRotation: 0,
                    format: (value) => formatCompactChartValue(value, formatMetricValue, financialValueDisplay),
                    legend: formatting.showAxisLabels ? getMetricLabel() : undefined,
                    legendOffset: -56,
                    legendPosition: 'middle',
                    truncateTickAt: 14,
                }}
                colors={colorsArray}
                theme={nivoChartTheme}
                pointSize={10}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabel='data.yFormatted'
                pointLabelYOffset={-12}
                enableCrosshair={false}
                enableArea={false}
                crosshairType='x'
                useMesh={true}
                enableSlices='x'
                markers={averageMarkers}
                tooltip={lineTooltip}
                sliceTooltip={sliceTooltip}
                legends={formatting.showLegend ? getBottomLegend({ translateY: 64 }) : []}
                // legends={[
                //     {
                //         anchor: 'bottom',
                //         direction: 'row',
                //         justify: false,
                //         translateX: 0,
                //         translateY: 0,
                //         itemsSpacing: 2,
                //         itemDirection: 'left-to-right',
                //         itemWidth: 80,
                //         itemHeight: 20,
                //         itemOpacity: 1,
                //         symbolSize: 12,
                //         symbolShape: 'circle',
                //         toggleSerie: true,
                //         symbolBorderColor: 'rgba(0, 0, 0, .5)',
                //         effects: [
                //             {
                //                 on: 'hover',
                //                 style: {
                //                     itemBackground: 'rgba(0, 0, 0, .03)',
                //                     itemOpacity: 1,
                //                 },
                //             },
                //         ],
                //     },
                // ]}
            />
        </div>
    );
};

export default LineChart;
